import { FC, Children, useRef, useLayoutEffect, forwardRef, useState } from 'react'
import { ReactComponent as AddEmojiIcon } from 'assets/icons/addEmoji16.svg'
import Styles from './ReactionPicker.module.scss'
import { ReactionPickerProps, QuickReactionsProps, QuickReactionProps } from './Types'
import { IconButton } from 'components/IconButton/IconButton'
import {
  offset,
  flip,
  shift,
  autoUpdate,
  useFloating,
  useInteractions,
  useHover,
  useFocus,
  useRole,
  useDismiss,
  safePolygon,
  FloatingPortal,
} from '@floating-ui/react-dom-interactions'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import Picker from '@emoji-mart/react'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { BaseEmoji } from 'types'

const QuickReaction = ({ emoji, label, onSelect }: QuickReactionProps) => {
  return (
    <Tooltip label={label} strategy="fixed">
      <div>
        <IconButton aria-label={label} size="md" onPress={() => onSelect(emoji)}>
          {emoji}
        </IconButton>
      </div>
    </Tooltip>
  )
}

const QuickReactions = forwardRef<HTMLDivElement, QuickReactionsProps>(
  ({ style, state, onOpenChange, onSelect, ...props }, ref) => {
    return (
      <div style={style} className={Styles.quickReactions} {...props} ref={ref}>
        <QuickReaction label="Done" emoji="✅" onSelect={onSelect} />
        <QuickReaction label="Taking a look" emoji="👀" onSelect={onSelect} />
        <QuickReaction label="On fire" emoji="🔥" onSelect={onSelect} />
        <QuickReaction label="Agree" emoji="👍" onSelect={onSelect} />
        <QuickReaction label="Love it" emoji="😍" onSelect={onSelect} />
        <QuickReaction label="Hilarious" emoji="😂" onSelect={onSelect} />

        <DropdownTrigger
          popoverClassName={Styles.popoverOverride}
          onClose={() => state?.close()}
          onOpenChange={onOpenChange}
        >
          <Tooltip label="Find another reaction" strategy="fixed">
            <div>
              <IconButton aria-label="Find another reaction" size="md" icon={AddEmojiIcon} />
            </div>
          </Tooltip>
          {(close) => (
            <Picker
              autoFocus
              theme="light"
              previewPosition="none"
              onEmojiSelect={async (emoji: BaseEmoji) => {
                onSelect(emoji.native)
                close()
              }}
            />
          )}
        </DropdownTrigger>
      </div>
    )
  },
)

const ReactionPicker: FC<React.PropsWithChildren<ReactionPickerProps>> = ({ children, onSelect }) => {
  const [open, setOpen] = useState(false)
  const [locked, setLocked] = useState(false)

  const ref = useRef<HTMLElement | null>(null)

  /* --------------------------- Tooltip positioning -------------------------- */

  const { x, y, reference, floating, strategy, context } = useFloating({
    strategy: 'fixed',
    placement: 'top',
    open,
    onOpenChange: setOpen,
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
    middleware: [offset(10), flip(), shift({ padding: 8 })],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      handleClose: safePolygon({
        blockPointerEvents: false,
      }),
      enabled: !locked,
    }),
    useFocus(context),
    useRole(context, { role: 'tooltip' }),
    useDismiss(context),
  ])

  useLayoutEffect(() => {
    reference(ref.current)
  }, [ref, reference])

  const style = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
  }

  /* -------------------------------------------------------------------------- */

  const trigger = children ? Children.only(children) : <IconButton aria-label="Add reaction" icon={AddEmojiIcon} />

  return (
    <div>
      <span {...getReferenceProps} ref={ref}>
        {trigger}
      </span>
      {open && (
        <FloatingPortal id="floating-ui-root">
          <QuickReactions
            ref={floating}
            style={style}
            onSelect={(emoji) => {
              onSelect(emoji)
              setLocked(false)
            }}
            onOpenChange={setLocked}
            {...getFloatingProps()}
          />
        </FloatingPortal>
      )}
    </div>
  )
}

export { ReactionPicker }
